import { DateRange, Schedule } from "@mui/icons-material";
import {
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  height: "36px",
  "& .MuiToggleButton-root": {
    border: "1px solid #e2e8f0",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#64748b",
    backgroundColor: "#ffffff",
    "&:hover": {
      backgroundColor: "#f8fafc",
      borderColor: "#cbd5e1",
    },
    "&.Mui-selected": {
      backgroundColor: "#3b82f6",
      color: "white",
      borderColor: "#3b82f6",
      "&:hover": {
        backgroundColor: "#2563eb",
      },
    },
  },
}));

const QuickChip = styled(Chip)(({ theme, selected }) => ({
  height: "28px",
  fontSize: "12px",
  fontWeight: 500,
  backgroundColor: selected ? "#3b82f6" : "#f8fafc",
  color: selected ? "white" : "#64748b",
  border: `1px solid ${selected ? "#3b82f6" : "#e2e8f0"}`,
  "&:hover": {
    backgroundColor: selected ? "#2563eb" : "#f1f5f9",
    borderColor: "#3b82f6",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: "36px",
    fontSize: "13px",
    backgroundColor: "#ffffff",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3b82f6",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    "&.Mui-focused": {
      color: "#3b82f6",
    },
  },
  "& .MuiInputBase-input": {
    padding: "8px 12px",
  },
}));

const EnhancedDatePicker = ({
  dateMode,
  setDateMode,
  months,
  setMonths,
  customDateRange,
  setCustomDateRange,
  range,
  setRange,
  sx = {},
}) => {
  const calculateDateRangeFromMonths = (months) => {
    const today = new Date();

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (months >= 24) {
      const yearsToGoBack = Math.floor(months / 12);
      const currentYear = today.getFullYear();

      const startYear = currentYear - yearsToGoBack + 1;
      const startOfPeriod = new Date(startYear, 0, 1);

      return {
        startDate: formatDate(startOfPeriod),
        endDate: formatDate(today),
      };
    }

    const startOfPeriod = new Date(
      today.getFullYear(),
      today.getMonth() - months + 1,
      1
    );

    return {
      startDate: formatDate(startOfPeriod),
      endDate: formatDate(today),
    };
  };

  const presetOptions = [
    { value: 1, label: "1M" },
    { value: 3, label: "3M" },
    { value: 6, label: "6M" },
    { value: 12, label: "1Y" },
    { value: 24, label: "2Y" },
    { value: 36, label: "3Y" },
  ];

  const handleDateModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDateMode(newMode);
      localStorage.setItem("dateMode", newMode);

      if (newMode === "custom") {
        setRange(customDateRange);
        //localStorage.setItem("range", JSON.stringify(customDateRange));
      } else {
        let dateRange = calculateDateRangeFromMonths(months);
        setRange(dateRange);
        localStorage.setItem("range", JSON.stringify(dateRange));
      }
    }
  };

  const handlePresetClick = (value) => {
    setMonths(value);
    let date = calculateDateRangeFromMonths(value);
    setRange(date);
    localStorage.setItem("range", JSON.stringify(date));
    localStorage.setItem("selectedMonths", value);
  };

  const handleCustomDateChange = (field, value) => {
    const newDateRange = {
      ...customDateRange,
      [field]: value,
    };

    if (value && !isNaN(new Date(value).getTime())) {
      setCustomDateRange(newDateRange);
      setRange(newDateRange);
      localStorage.setItem("customDateRange", JSON.stringify(newDateRange));
      localStorage.setItem("range", JSON.stringify(newDateRange));
      //console.log('Custom date updated:', field, value, 'Full range:', newDateRange);
    } else {
      //console.error('Invalid date value:', value);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, ...sx }}>
      {/* Mode Toggle */}
      <StyledToggleButtonGroup
        value={dateMode}
        exclusive
        onChange={handleDateModeChange}
        size="small"
      >
        <ToggleButton value="preset">
          <Schedule sx={{ fontSize: "16px", mr: 0.5 }} />
          Preset
        </ToggleButton>
        <ToggleButton value="custom">
          <DateRange sx={{ fontSize: "16px", mr: 0.5 }} />
          Custom
        </ToggleButton>
      </StyledToggleButtonGroup>

      {/* Content */}
      {dateMode === "preset" ? (
        <Box display="flex" alignItems="center" gap={1}>
          {presetOptions.map((option) => (
            <QuickChip
              key={option.value}
              label={option.label}
              selected={months === option.value}
              onClick={() => handlePresetClick(option.value)}
              clickable
            />
          ))}
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={1}>
          <StyledTextField
            type="date"
            label="From"
            value={customDateRange.startDate}
            onChange={(e) =>
              handleCustomDateChange("startDate", e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: "140px" }}
          />
          <Typography variant="body2" sx={{ color: "#94a3b8", mx: 0.5 }}>
            to
          </Typography>
          <StyledTextField
            type="date"
            label="To"
            value={customDateRange.endDate}
            onChange={(e) => handleCustomDateChange("endDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: "140px" }}
          />
        </Box>
      )}
    </Box>
  );
};

export default EnhancedDatePicker;
